import {groupBy} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {AnnotationData} from 'molbio/annotations/AnnotationUtil';
import ClearSearchButton from 'search/ClearSearchButton';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

interface AnnotationTypeVisibilityDropdownProps {
  annotations: Array<AnnotationData>;
  onToggleVisibility: (ids: Array<string>, visible: boolean) => void;
  useCompactSize?: boolean;
}

/**
 * Renders a dropdown for toggling the visibility of different types of annotations.
 *
 * @param annotations List of all annotations for the sequence.
 * @param onToggleVisibility Callback issued when visibility for an annotation type is toggled.
 * @constructor
 */
export default function AnnotationTypeVisibilityDropdown({
  annotations,
  onToggleVisibility,
  useCompactSize = false,
}: AnnotationTypeVisibilityDropdownProps): JSX.Element {
  const hiddenTypes: Array<string> = [];
  const allTypes: Array<string> = [];
  const annotationsByType = groupBy(annotations, (annotation: AnnotationData) => annotation.annotation_type);
  for (const [type, items] of Object.entries(annotationsByType)) {
    // Skip empty and null types.
    if (!type) {
      continue;
    }
    if (items.every((annotation) => !annotation.visible)) {
      hiddenTypes.push(type);
    }
    allTypes.push(type);
  }

  const optionDatas: Array<OptionData> = [...allTypes].map((type) => ({
    name: type,
    value: type,
    icon: 'check',
    iconClassName: hiddenTypes.includes(type) ? 'is-selected' : undefined,
  }));
  const filterMenu = (
    <SearchableMenu
      scrollable={true}
      isMulti={true}
      onSelect={(type: string) => {
        onToggleVisibility(
          annotationsByType[type].map((a) => a.cid),
          hiddenTypes.includes(type)
        );
      }}
      selectedValue={[...hiddenTypes]}
      optionDatas={optionDatas}
      getMatchingOptionDatas={(query) => optionDatas.filter((el) => el.value.includes(query))}
      height={MenuHeight.MEDIUM}
      width={MenuWidth.MEDIUM}
    />
  );

  function clearFilters(): void {
    onToggleVisibility(
      annotations.map((a) => a.cid),
      true
    );
  }

  return (
    <div className={css(styles.filterContainer)}>
      <Dropdown menu={filterMenu} closeOnInsideClick={false}>
        <Button
          size={ButtonSize.SMALL}
          buttonStyle={hiddenTypes ? ButtonStyle.TERTIARY_GRAY : ButtonStyle.TERTIARY_BLUE_POWDER}
          text={
            hiddenTypes.length === 0
              ? 'Visibility filter'
              : `Visibility filter: ${hiddenTypes.length} ${pluralize(hiddenTypes.length, 'type')} hidden`
          }
          isDropdown={true}
        />
      </Dropdown>
      {hiddenTypes.length > 0 &&
        (useCompactSize ? (
          <BenchlingIcon className={css(styles.clearIcon)} icon="panel-hidden" onClick={clearFilters} />
        ) : (
          <div className={css(styles.clearButton)}>
            <ClearSearchButton onClear={clearFilters} />
          </div>
        ))}
    </div>
  );
}

const styles = StyleSheet.create({
  filterContainer: {
    display: 'flex',
  },
  clearIcon: {
    width: '14px',
    height: '14px',
    color: GRAY_DARKER,
    margin: 'auto 0 auto 4px',
    cursor: 'pointer',
  },
  clearButton: {
    margin: 'auto 0 auto 4px',
  },
});
